// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Orders  (route /orders/:ref — "Order confirmed" right after payment, "Order details" afterwards)
// Everything recorded about one order: status + order id (copyable) + amount paid + when it was placed; the delivery
// address and the customer's delivery notes; the real status timeline (times only for steps that happened); every
// item with pack size, cut, quantity and price; the full bill (items, delivery fee, welcome coupon, total paid) and
// the payment reference and invoice number; a refund note for a cancelled order; Cancel (only before ZenFresh confirms it),
// View invoice and Re-order. Reached from checkout (router state `justPlaced`) and from My Orders.
// While the order is active the page refreshes itself so a status change made by staff shows up without a reload.
// Deliberately NOT here (nothing supports it): arrival window / slot, live temperature, van tracking, "Sub-Zero"
// packing, WhatsApp dispatch alerts toggle, eco-box refund, formalin-free guarantee, a GST *tax* invoice (no GST data yet).
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, Check, Copy, FileText, MapPin, RotateCcw, ShoppingBag, Star } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import CancelOrder from "../components/orders/CancelOrder";
import { OrderLines, OrderTimeline, RefundNote, StatusBadge } from "../components/orders/OrderParts";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { ApiError } from "../utils/api";
import { fetchOrder, formatWhen, packCount, paidVia, type Order } from "../utils/orders";
import { formatInr } from "../utils/priceFormat";

type Load = { status: "loading" } | { status: "missing" } | { status: "error" } | { status: "ready"; order: Order };
const REFRESH_MS = 30_000;

export default function OrderDetail() {
  const { ref = "" } = useParams();
  const justPlaced = (useLocation().state as { justPlaced?: boolean } | null)?.justPlaced === true;
  const auth = useAuth();
  const signedIn = auth.status === "signed-in";
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [copied, setCopied] = useState(false);
  useDocumentTitle(justPlaced ? "Order confirmed" : "Order details");

  const refresh = useCallback(async () => {
    try {
      setLoad({ status: "ready", order: await fetchOrder(ref) });
    } catch (error) {
      setLoad((current) => (current.status === "ready" ? current : error instanceof ApiError && error.status === 404 ? { status: "missing" } : { status: "error" }));
    }
  }, [ref]);

  useEffect(() => {
    if (!signedIn) return;
    void refresh();
  }, [signedIn, refresh]);

  const active = load.status === "ready" && load.order.active;
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => document.visibilityState === "visible" && void refresh(), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [active, refresh]);

  if (auth.status === "signed-out") return <Navigate to="/welcome" replace />;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ref);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access refused: the id is on screen to copy by hand.
    }
  };

  const shell = (children: React.ReactNode) => (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs dynamicLabel={ref} />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">{children}</div>
      </div>
    </PageTransition>
  );

  if (load.status === "loading") {
    return shell(<p role="status" className="rounded-2xl bg-white p-6 text-center text-sm shadow-sm dark:bg-ocean-900">Loading your order…</p>);
  }
  if (load.status === "missing" || load.status === "error") {
    return shell(
      <div role="alert" className="rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-ocean-900">
        <p className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50">
          {load.status === "missing" ? "We couldn't find that order" : "We couldn't load this order"}
        </p>
        <p className="mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">
          {load.status === "missing" ? "It may belong to another account, or the reference is wrong." : "Check your connection and try again."}
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {load.status === "error" && (
            <button type="button" onClick={() => void refresh()} className="min-h-11 rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700">
              Try again
            </button>
          )}
          <Link to="/orders" className="inline-flex min-h-11 items-center rounded-full px-6 text-sm font-bold text-ocean-900 ring-1 ring-ocean-900/15 hover:bg-ocean-700/5 dark:text-foam-100">
            My orders
          </Link>
        </div>
      </div>,
    );
  }

  const order = load.order;
  const d = order.delivery;
  const packs = packCount(order);

  return shell(
    <>
      {justPlaced ? (
        <header className="flex flex-col items-center text-center">
          <span aria-hidden="true" className="flex h-20 w-20 items-center justify-center rounded-full bg-teal-600 text-white shadow-lg">
            <Check className="h-10 w-10" />
          </span>
          <h1 className="mt-4 font-display text-3xl font-bold text-ocean-950 dark:text-foam-50">Order confirmed!</h1>
          <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">
            Your payment of {formatInr(order.total)} was received. We&apos;ll update the status below as your order moves along.
          </p>
        </header>
      ) : (
        <header className="flex items-center gap-3">
          <Link to="/orders" aria-label="Back to my orders" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </Link>
          <h1 className="font-display text-3xl font-bold text-ocean-950 dark:text-foam-50">Order details</h1>
        </header>
      )}

      <section aria-label="Order summary" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <StatusBadge status={order.status} />
          <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">Placed {formatWhen(order.createdAt)}</p>
        </div>
        <p className="mt-3 flex flex-wrap items-center gap-2 text-sm text-ocean-800/85 dark:text-foam-100/80">
          Order ID:
          <span className="font-mono font-bold text-ocean-950 dark:text-foam-50">{order.ref}</span>
          <button
            type="button"
            onClick={() => void copy()}
            className="inline-flex min-h-9 items-center gap-1 rounded-full px-3 text-xs font-bold text-teal-700 ring-1 ring-teal-600/30 hover:bg-teal-100/60 dark:text-teal-300"
          >
            {copied ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </p>
        <p role="status" className="sr-only">{copied ? "Order ID copied" : ""}</p>
        <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">
          Paid <strong className="text-ocean-950 dark:text-foam-50">{formatInr(order.total)}</strong> {paidVia(order)} · {packs} {packs === 1 ? "pack" : "packs"} from {order.items.length} {order.items.length === 1 ? "catch" : "catches"}
        </p>
      </section>

      {order.status === "cancelled" && <RefundNote order={order} />}

      <section aria-labelledby="progress-heading" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
        <h2 id="progress-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Order progress</h2>
        <div className="mt-3"><OrderTimeline order={order} /></div>
      </section>

      <section aria-labelledby="items-heading" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
        <h2 id="items-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Catch &amp; cut summary ({order.items.length} {order.items.length === 1 ? "item" : "items"})</h2>
        <div className="mt-3"><OrderLines order={order} /></div>
      </section>

      <section aria-labelledby="bill-heading" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
        <h2 id="bill-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Bill &amp; payment</h2>
        <dl className="mt-3 space-y-2 text-sm">
          <div className="flex justify-between"><dt className="text-ocean-800/85 dark:text-foam-100/80">Items (incl. cut prep)</dt><dd className="font-semibold">{formatInr(order.subtotal)}</dd></div>
          <div className="flex justify-between"><dt className="text-ocean-800/85 dark:text-foam-100/80">Delivery fee</dt><dd className="font-semibold">{order.deliveryFee === 0 ? "FREE" : formatInr(order.deliveryFee)}</dd></div>
          {order.discount > 0 && (
            <div className="flex justify-between text-teal-700 dark:text-teal-300"><dt>Welcome coupon{order.couponCode ? ` (${order.couponCode})` : ""}</dt><dd className="font-semibold">−{formatInr(order.discount)}</dd></div>
          )}
          <div className="flex justify-between border-t border-ocean-900/10 pt-2 dark:border-white/10"><dt className="font-bold">Total paid</dt><dd className="font-display text-lg font-bold">{formatInr(order.total)}</dd></div>
          {order.walletAmount > 0 && (
            <div className="flex justify-between text-xs text-ocean-800/85 dark:text-foam-100/80"><dt>of which from your wallet</dt><dd>{formatInr(order.walletAmount)}</dd></div>
          )}
          {order.payment && order.walletAmount > 0 && (
            <div className="flex justify-between text-xs text-ocean-800/85 dark:text-foam-100/80"><dt>of which paid online</dt><dd>{formatInr(order.total - order.walletAmount)}</dd></div>
          )}
        </dl>
        <p className="mt-3 text-xs text-ocean-800/75 dark:text-foam-100/70">
          {order.payment ? (
            <>Paid {paidVia(order)} through Razorpay · payment ID <span className="font-mono">{order.payment.paymentId}</span></>
          ) : (
            <>Paid in full from your ZenFresh wallet</>
          )}
          {order.invoiceNumber && <> · invoice <span className="font-mono">{order.invoiceNumber}</span></>}
        </p>
        <Link to={`/orders/${order.ref}/invoice`} className="mt-3 inline-flex min-h-11 items-center gap-1.5 rounded-full px-4 text-sm font-bold text-teal-700 ring-1 ring-teal-600/30 hover:bg-teal-100/60 dark:text-teal-300">
          <FileText className="h-4 w-4" aria-hidden="true" /> View invoice
        </Link>
      </section>

      <section aria-labelledby="deliver-heading" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
        <h2 id="deliver-heading" className="flex items-center gap-2 font-display text-lg font-bold text-ocean-950 dark:text-foam-50">
          <MapPin className="h-5 w-5 text-teal-700 dark:text-teal-300" aria-hidden="true" /> Delivery address &amp; notes
        </h2>
        <p className="mt-2 flex flex-wrap justify-between gap-x-3 text-sm">
          <span className="font-bold text-ocean-950 dark:text-foam-50">{d.name}</span>
          <span className="text-ocean-800/85 dark:text-foam-100/80">{d.phone}</span>
        </p>
        <p className="text-sm text-ocean-800/90 dark:text-foam-100/85">{d.addressLine}, {d.city} {d.pincode}</p>
        {d.notes.length > 0 && (
          <p className="mt-2 rounded-lg bg-ocean-700/5 px-3 py-2 text-xs text-ocean-800/85 dark:bg-white/10 dark:text-foam-100/80">
            Your delivery notes: {d.notes.join(" · ")}. These are requests to the courier.
          </p>
        )}
      </section>

      <div className="flex flex-col gap-2">
        {order.status === "delivered" && (
          <Link to={`/orders/${order.ref}/review`} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-white px-6 text-sm font-bold text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100">
            <Star className={`h-4 w-4 text-coral-500 ${order.review ? "fill-current" : ""}`} aria-hidden="true" />
            {order.review ? `Your review: ${order.review.overall} of 5 stars` : "Rate & review this order"}
          </Link>
        )}
        {order.canCancel && (
          <div className="flex flex-wrap justify-center">
            <CancelOrder order={order} onChanged={(updated) => setLoad({ status: "ready", order: updated })} />
          </div>
        )}
        <Link to={`/orders/${order.ref}/reorder`} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600">
          <RotateCcw className="h-4 w-4" aria-hidden="true" /> Re-order these items
        </Link>
        <Link to="/menu" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-white px-6 text-sm font-bold text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100">
          <ShoppingBag className="h-4 w-4" aria-hidden="true" /> Back to the menu
        </Link>
        <Link to="/orders" className="text-center text-sm font-semibold text-teal-700 underline underline-offset-2 dark:text-teal-300">View all my orders</Link>
      </div>

      <p className="text-center text-xs text-ocean-800/75 dark:text-foam-100/70">
        Need to change an order? <Link to="/contact" className="font-semibold underline">Contact us</Link> and quote <span className="font-mono">{order.ref}</span>.
      </p>
    </>,
  );
}
